import { FC } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  formattedCurrency,
  InteracBulkPayoutTransactionResponse,
  withStyles,
  capitalizeFirstLetter,
  InteracWordmark,
  TransactionType,
} from 'vopay';
import { TransactionCard, Title, DetailsRows, TransactionStatus, UtilButton } from 'UI';
import { useClientData } from '@/providers/ClientData.provider';
import ApprovalHistoryDetailsCard from './Card/ApprovalHistoryDetailsCard';
import styles from '../Transactions.module.scss';

const cn = withStyles(styles);

type InteracBulkPayoutTransactionInfoProps = {
  transaction: InteracBulkPayoutTransactionResponse;
  Notes: string;
  isParent?: boolean;
};

type DataProps = {
  hasErrorInfo: boolean;
  failureReason: string;
  accountId: string;
};

const InteracBulkPayoutTransactionInfo: FC<InteracBulkPayoutTransactionInfoProps> = ({
  transaction,
  Notes,
  isParent = false,
}) => {
  const { state } = useLocation<DataProps>();
  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Details.InteracBulkPayoutTransactionInfo',
  });
  const { t: tGlobal } = useTranslation('translations');
  const {
    response: { clientAccountsEnabled },
  } = useClientData();

  const transactionDetails = [
    {
      label: t('TransactionDetails.transactionStatus', { defaultValue: 'Transaction Status' }),
      value: (
        <Box jc="fe">
          <TransactionStatus
            status={transaction.TransactionStatus}
            label={capitalizeFirstLetter(
              tGlobal(`Transaction.TransactionHistory.statuses.${transaction.TransactionStatus}`, {
                defaultValue: transaction.TransactionStatus,
              }),
            )}
          />
        </Box>
      ),
      visible: isParent,
    },
    {
      label: t('TransactionDetails.type', { defaultValue: 'Transaction Type' }),
      value: <InteracWordmark type="bulkPayout" />,
    },
    {
      label: t('TransactionDetails.transactionTypeCode', { defaultValue: 'Transaction Type Code' }),
      value: transaction.TransactionTypeCode || '-',
    },
    {
      label: t('TransactionDetails.transactionDate', { defaultValue: 'Transaction Date' }),
      value: transaction.TransactionDateTime,
    },
    {
      label: t('TransactionDetails.lastModified', { defaultValue: 'Last Modified' }),
      value: transaction.LastModified,
    },
    {
      label: t('TransactionDetails.amount', { defaultValue: 'Amount' }),
      value: transaction.Currency + ' ' + formattedCurrency(transaction.Amount),
    },
    {
      label: t('TransactionDetails.holdAmount', { defaultValue: 'Hold Amount' }),
      value: transaction.Currency + ' ' + formattedCurrency(transaction.HoldAmount),
    },
    {
      label: t('TransactionDetails.clientReferenceNumber', { defaultValue: 'Client Reference Number' }),
      value: transaction.ClientReferenceNumber || '-',
    },
    {
      label: t('TransactionDetails.glCode', { defaultValue: 'GL Code' }),
      value: transaction.GLCode || '-',
    },
    {
      label: t('TransactionDetails.notes', { defaultValue: 'Notes' }),
      value: Notes || '-',
    },
    {
      label: t('ErrorInfo.failureReason', { defaultValue: 'Failure Reason' }),
      value: state?.failureReason || '-',
      visible: isParent && state.failureReason !== '',
    },
  ];

  const eTransferDetails = [
    {
      label: t('ETransferDetails.recipientName', { defaultValue: 'Recipient Name' }),
      value:
        state && state.accountId && state.accountId !== '' ? (
          <UtilButton
            onClick={() => {
              if (clientAccountsEnabled) {
                window.open(`/client-accounts/${state.accountId}`, '_blank');
              } else {
                window.open(`/contacts/${state.accountId}`, '_blank');
              }
            }}
          >
            {transaction.RecipientName}
          </UtilButton>
        ) : (
          transaction.RecipientName
        ),
    },
    {
      label: t('ETransferDetails.email', { defaultValue: 'Email' }),
      value: transaction.EmailAddress,
      visible: transaction.EmailAddress !== '',
    },
    {
      label: t('ETransferDetails.phone', { defaultValue: 'Phone' }),
      value: transaction.PhoneNumber,
      visible: transaction.PhoneNumber !== '',
    },
    {
      label: t('ETransferDetails.messageForRecipient', { defaultValue: 'Message For Recipient' }),
      value: transaction.Memo || '-',
    },
    {
      label: t('ETransferDetails.confirmationNumber', { defaultValue: 'Confirmation Number' }),
      value: transaction.ConfirmationNumber || '-',
    },
    {
      label: t('ETransferDetails.expirationTime', { defaultValue: 'Expiration Time' }),
      value: transaction.CancellationPeriod + ' ' + t('ETransferDetails.days') || '-',
    },
  ];

  const errorDetails = [
    {
      label: t('ErrorInfo.failureDateTime', { defaultValue: 'Failure Date Time' }),
      value: transaction.LastModified,
    },
    {
      label: t('ErrorInfo.amount', { defaultValue: 'Amount' }),
      value: `${transaction.Currency} ${formattedCurrency(transaction.Amount)}`,
    },
    {
      label: t('ErrorInfo.failureReason', { defaultValue: 'Failure Reason' }),
      value: state?.failureReason || '-',
      visible: !isParent,
    },
  ];

  return (
    <Box flx="col" gapSize="24px">
      {!isParent && (
        <TransactionCard
          receiverName={transaction.RecipientName}
          amount={transaction.Amount}
          currency={transaction.Currency}
          transactionId={transaction.TransactionID}
          type={'Interac Bulk Payout'}
          failureReason={transaction.FailureReason}
          contactId={state?.accountId}
        />
      )}

      <Card className={cn('card')}>
        <Title level="4" className={cn('title')}>
          {t('transactionDetailsTitle', { defaultValue: 'Transaction Details' })}
        </Title>
        <DetailsRows className={cn('rows')} details={transactionDetails} />
      </Card>

      <ApprovalHistoryDetailsCard
        transactionID={transaction.TransactionID}
        status={transaction.TransactionStatus}
        type={TransactionType.INTERACT_BULK_PAYOUT}
      />

      <Card className={cn('card')}>
        <Title level="4" className={cn('title')}>
          <InteracWordmark type="bulkPayout" />{' '}
          {t('eTransferDetailsTitle', { defaultValue: 'Interac e-Transfer® Bulk Payout Details' })}
        </Title>
        <DetailsRows className={cn('rows')} details={eTransferDetails} />
      </Card>

      {transaction.FailureReason && (
        <Card className={cn('card')}>
          <Title level="4" className={cn('title')}>
            {t('errorInfoTitle', { defaultValue: 'Error Info' })}
          </Title>
          <DetailsRows className={cn('rows')} details={errorDetails} />
        </Card>
      )}
    </Box>
  );
};

export default InteracBulkPayoutTransactionInfo;
